/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  QuickLRU with ttl expiration
 */

import { SyncMap, SyncMapEntry, SyncMapForEachCallback } from '@zcode/async-map'
import QuickLRU from 'quick-lru'

import { filterMapIterable } from './filterMapIterable'
import { isValidStoredData } from './helpers/isValidStoredData'
import { toStoredData } from './helpers/toStoredData'
import { toValue } from './helpers/toValue'
import { StoredData } from './types/StoredData'

import { getExpiresInMs } from './helpers/getExpiresInMs'
import { ExpirableLRUOptions } from './types/ExpirableLRUOptions'

export class ExpirableLRU<TKey = any, TValue = any>
  implements SyncMap<TKey, TValue>
{
  protected quickLRU: QuickLRU<TKey, StoredData<TValue>>
  protected options: ExpirableLRUOptions<TKey, TValue>

  constructor(options: ExpirableLRUOptions<TKey, TValue>) {
    this.options = { ...options }
    this.quickLRU = new QuickLRU<TKey, StoredData<TValue>>(options)
  }

  stringTag() {
    return '[object ExpireableLRU]'
  }

  get [Symbol.toStringTag]() {
    return this.stringTag()
  }

  // The stored item count. Does not filter expired
  get size() {
    // TODO: does not filter expired
    return this.quickLRU.size
  }

  get version() {
    return this.options.version
  }

  set version(value: ExpirableLRUOptions['version']) {
    this.options.version = value
  }

  /**
   * Set an item. Returns the instance.
   *
   * @param {any} key -
   * @param {any} value -
   * @param {any} ttl -
   * @returns {ExpirableLRU} -
   */
  set(key: TKey, value: TValue, ttl?: number) {
    const options = ttl !== undefined ? { ...this.options, ttl } : this.options
    this.quickLRU.set(key, toStoredData(value, options))
    return this
  }

  /**
   * Get an item. Deletes it if invalid.
   *
   * @param {any} key - element's key
   * @returns {boolean} -
   */
  get(key: TKey) {
    const storedData = this.quickLRU.get(key)
    if (storedData && !isValidStoredData(storedData, this.options)) {
      this.quickLRU.delete(key)
      return
    }
    return storedData ? toValue(storedData, this.options) : undefined
  }

  getExpiresInMs(key: TKey) {
    const storedData = this.quickLRU.peek(key)
    if (storedData) {
      return getExpiresInMs(storedData, this.options)
    }
    return null
  }

  /**
   * Peek an item. Deletes it if invalid.
   *
   * @param {any} key - element's key
   * @returns {any} - element's value or undefined
   */
  peek(key: TKey) {
    const storedData = this.quickLRU.peek(key)
    if (storedData && !isValidStoredData(storedData, this.options)) {
      this.quickLRU.delete(key)
      return
    }
    return storedData ? toValue(storedData, this.options) : undefined
  }

  /**
   * Check if an item exists. Deletes it if invalid.
   *
   * @param {any} key - element's key
   * @returns {boolean} -
   */
  has(key: TKey) {
    const has = this.quickLRU.has(key)
    if (has) {
      const storedData = this.quickLRU.peek(key)
      const valid =
        storedData !== undefined
          ? isValidStoredData(storedData, this.options)
          : false
      if (storedData && !valid) {
        this.quickLRU.delete(key)
      }
      return valid
    }
    return has
  }

  /**
   * Delete an item.
   * Returns true if the item is removed or false if the item doesn't exist.
   *
   * @param {any} key - element's key
   * @returns {boolean} -
   */
  delete(key: TKey) {
    return this.quickLRU.delete(key)
  }

  clear() {
    return this.quickLRU.clear()
  }

  forEach(callback: SyncMapForEachCallback<TKey, TValue>, thisArg?: any) {
    for (const [key, value] of this.entries()) {
      callback.call(thisArg, value, key, this)
    }
  }

  // Iterable for all the entries.
  entries() {
    const keys = this.quickLRU.keys()
    return filterMapIterable<TKey, SyncMapEntry<TKey, TValue>>(
      keys,
      (key) => this.has(key),
      (key) => [key, this.get(key)] as SyncMapEntry<TKey, TValue>
    )
  }

  // Iterable for all the keys.
  keys() {
    const keys = this.quickLRU.keys()
    return filterMapIterable<TKey>(keys, (key) => this.has(key))
  }

  // Iterable for all the values.
  values() {
    const values = this.quickLRU.values()
    return filterMapIterable<StoredData<TValue>, TValue>(
      values,
      (storedData) => isValidStoredData<TValue>(storedData, this.options),
      (storedData) => toValue<TValue>(storedData, this.options)
    )
  }

  *[Symbol.iterator]() {
    yield* this.entries()
  }
}
